import { Banner } from 'components/shared/ScheduledRun/Blotter/panel/Shared/Banner';
import { ConnectedStatusFooter } from 'components/shared/ScheduledRun/Blotter/panel/Shared/StatusFooter';
import type { IntlKey } from 'locales';
import { FormattedMessage } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import {
  EDITABLE_SCHEDULED_RUN_BLOTTER_ID,
  getEditableScheduledRun,
  resetRunThunk,
  sendResetRunThunk,
} from 'state/blotters/scheduledQuotes';
import { scheduledRunActions } from 'state/scheduledRuns/actions';
import type { ConfirmBannerType } from 'state/scheduledRuns/state';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { TypedError } from 'utils/errors/errors';
import type { LoadableContent } from 'utils/status';

export interface EditableFooterDispatchProps {
  onEditClicked: () => void;
  onDiscardUpdate: () => void;
  onConfirmUpdate: () => void;

  onClose: () => void;
  onConfirmBannerChange: (banner?: ConfirmBannerType) => void;
}

export interface EditableFooterStateProps {
  isEditing?: boolean;
  updateRun: LoadableContent<undefined, TypedError>;

  hasChanged?: boolean;

  confirmBanner?: ConfirmBannerType;
}
interface OwnProps {
  editBtnLabel: IntlKey;
  editBtnIcn: string;

  errorLabel: IntlKey;
  confirmMessageLabel: IntlKey;

  analyticAction: 'Adjust schedule' | 'Manage quotes';
}
export interface Props extends EditableFooterStateProps, EditableFooterDispatchProps, OwnProps {}

export const editableFooterMapDispatchToProps = (
  dispatch: ThunkDispatch,
): Pick<EditableFooterDispatchProps, 'onConfirmBannerChange' | 'onDiscardUpdate' | 'onClose'> => ({
  onConfirmBannerChange(banner) {
    dispatch(scheduledRunActions.setConfirmBanner(EDITABLE_SCHEDULED_RUN_BLOTTER_ID, banner));
  },
  onDiscardUpdate() {
    dispatch(resetRunThunk());
  },
  onClose() {
    dispatch(sendResetRunThunk());
  },
});
export const editableFooterMapStateToProps = (
  appState: AppState,
): Pick<EditableFooterStateProps, 'confirmBanner' | 'updateRun'> => ({
  confirmBanner: getEditableScheduledRun(appState).confirmBanner,
  updateRun: getEditableScheduledRun(appState).updateRun,
});
export function EditableFooter({
  isEditing,
  onEditClicked,
  onDiscardUpdate,
  onConfirmUpdate,
  updateRun,
  editBtnIcn,
  editBtnLabel,
  hasChanged,
  confirmMessageLabel,
  onConfirmBannerChange,
  confirmBanner,
  errorLabel,
  onClose,
  analyticAction,
}: Props) {
  return (
    <div className="d-flex w-100 flex-wrap">
      {!isEditing && (
        <>
          {!confirmBanner && (
            <button
              className="btn btn-outline-primary  btn-icon btn-icon-end btn-lg w-100 mx-4 mb-1"
              onClick={() => {
                onEditClicked();
                logAnalytics.trackEvent('Scheduled run', analyticAction);
              }}
            >
              <FormattedMessage id={editBtnLabel} />
              <i className="icon icon-lg">{editBtnIcn}</i>
            </button>
          )}
          <ConnectedStatusFooter />
        </>
      )}
      {isEditing && !confirmBanner && (
        <div className="d-flex flex-fill mb-4 mx-4">
          <button
            className="btn btn-secondary btn-lg flex-grow-1 me-1"
            onClick={onDiscardUpdate}
            disabled={updateRun.status === 'loading'}
          >
            <FormattedMessage id="discard" />
          </button>
          <button
            className="btn btn-info btn-lg flex-grow-1 ms-1"
            onClick={onConfirmUpdate}
            disabled={updateRun.status === 'loading' || !hasChanged}
          >
            <FormattedMessage id="confirm" />
          </button>
        </div>
      )}
      {isEditing && confirmBanner === 'update' && (
        <Banner
          onConfirm={onConfirmUpdate}
          onDiscard={() => onConfirmBannerChange(undefined)}
          confirmMessageLabel={confirmMessageLabel}
          colorLevel="info"
          {...updateRun}
          errorLabel={errorLabel}
          onClose={onClose}
        />
      )}
    </div>
  );
}
